import { ReactNode } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import AssetDetail from '@/components/Assets/AssetDetails/AssetDetail';
import { getWasElectricAppInstalledOnDevice } from '@/components/Assets/helpers/getEnrollment';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { Divider, Flex, spaces, useBreakpoint } from '@electricjs/arc';
import {
  AssetsMdmStatus,
  EntitlementSlugEnum,
} from '@electricjs/core_entity-client';
import { DetailsContainer } from '@/components/Assets/AssetDetails/common';
import { DeviceAsset, isDesktopOrLaptopAsset } from '@/types/assets';
import formatDate from '@/helpers/formatDate';
import { getLocalizedSupportUrl } from '@/helpers/getLocalizedSupportUrl';
import { DetailSection } from '@/pages/Assets/AssetDetailsContent/DetailSection';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSystemWithPolicyStatsBySerialNumberQuery } from '@/redux/slices/mdmManagementApiSlice';
import { CenteredSpinner, LinkNoUnderline } from '@common';
import { getOsFullName } from 'common/utils/getOsFullName';
import { AssetDetailCollapsible } from '@/components/Assets/AssetDetails/AssetDetailCollapsible';
import { OsVersionDetail } from '@/types/mdmManagement';
import { AssetDetailsBaseCard } from '@/pages/Assets/AssetDetailsContent/AssetDetailsBaseCard';
import { DeviceNotEnrolledBanner } from '@/pages/Assets/AssetDetailsContent/DeviceNotEnrolledBanner';
import { DeviceNotApplicableBanner } from '@/pages/Assets/AssetDetailsContent/DeviceNotApplicableBanner';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DeviceManagedExternallyBanner } from './DeviceManagedExternallyBanner';

const AssetDetailItem = ({
  label,
  value,
  labelTooltip,
}: {
  label: string | ReactNode;
  value: string | ReactNode;
  labelTooltip?: string;
}) => {
  return (
    <AssetDetail
      label={label}
      labelVariant="label-small"
      labelTooltip={labelTooltip}
      value={value}
      valueVariant="body-2"
    />
  );
};

const getOsDetails = (
  osVersionDetail: OsVersionDetail | undefined,
  t: TFunction,
  osDisplayNameFallback?: string
) => {
  if (!osVersionDetail) {
    return '-';
  }

  const osName =
    osVersionDetail.osName?.trim() ||
    osVersionDetail.distributionName?.trim() ||
    osDisplayNameFallback?.trim() ||
    '-';

  const entries: Array<{ label: string; value: string | number | undefined }> =
    [
      {
        label: t('assets:assetDetailsContent.osDetails.osMajor'),
        value: osVersionDetail.majorNumber,
      },
      {
        label: t('assets:assetDetailsContent.osDetails.osMinor'),
        value: osVersionDetail.minorNumber,
      },
      { label: t('assets:assetDetailsContent.osDetails.name'), value: osName },
      {
        label: t('assets:assetDetailsContent.osDetails.patch'),
        value: osVersionDetail.patchNumber,
      },
      {
        label: t('assets:assetDetailsContent.osDetails.releaseName'),
        value: osVersionDetail.releaseName,
      },
      {
        label: t('assets:assetDetailsContent.osDetails.revision'),
        value: osVersionDetail.revision,
      },
      {
        label: t('assets:assetDetailsContent.osDetails.version'),
        value: osVersionDetail.version,
      },
    ];

  // Only show os detail fields that have a value; hide undefined, null, and empty string
  const osDetails = entries
    .filter(
      ({ value }) => value !== undefined && value !== null && value !== ''
    )
    .map(({ label, value }) => `${label}: ${value}`);

  return osDetails.length > 0 ? osDetails.join(', ') : '-';
};

type DeviceManagementDetailsCardProps = {
  deviceAsset: DeviceAsset;
  fullWidth?: boolean;
};

const DeviceManagementDetailsCard = ({
  deviceAsset,
  fullWidth,
}: DeviceManagementDetailsCardProps) => {
  const { t } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const { data: systemWithPolicyStats, isLoading } =
    useGetSystemWithPolicyStatsBySerialNumberQuery(
      deviceAsset.serialNumber && !deviceAsset.device.mdmProviderId
        ? {
            organizationId: organizationId,
            serialNumber: deviceAsset.serialNumber,
          }
        : skipToken
    );

  const { lg } = useBreakpoint();
  const { space_s, space_m, space_l } = spaces;
  const hasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );
  const {
    ithEnableNotApplicableMdmFlagForDevices,
    hideElectricAppInstalledIndicator,
  } = useFlags();

  const isMdmActive = systemWithPolicyStats?.system?.active;

  const osVersionDetail = systemWithPolicyStats?.system?.osVersionDetail;
  const osDisplayNameFromReporting =
    getOsFullName({
      osName: deviceAsset?.device?.reportingData?.osName,
      osVersion: deviceAsset?.device?.reportingData?.osVersion,
      osType: deviceAsset?.device?.osType,
    }) || undefined;

  const osDisplayNameFromMdm =
    osVersionDetail?.osName?.trim() ||
    osVersionDetail?.distributionName?.trim() ||
    undefined;
  const osDisplayName =
    osDisplayNameFromMdm || osDisplayNameFromReporting || '-';

  const osVersion =
    osVersionDetail?.version ??
    deviceAsset?.device?.reportingData?.osVersion ??
    '-';
  const osName = osVersionDetail
    ? osDisplayNameFromMdm
      ? `${osDisplayName} Version ${osVersion}`
      : osDisplayName
    : '-';

  if (isLoading) {
    return (
      <AssetDetailsBaseCard
        title={t('assetDetailsContent.deviceManagement')}
        icon="laptop">
        <CenteredSpinner />
      </AssetDetailsBaseCard>
    );
  }

  // If there is an MDM Provider ID specified then show custom panel
  if (deviceAsset.device.mdmProviderId) {
    return (
      <AssetDetailsBaseCard
        title={t('assetDetailsContent.deviceManagement')}
        icon="laptop">
        <Flex p={space_m} minHeight="250px" alignItems="center">
          <DeviceManagedExternallyBanner asset={deviceAsset} />
        </Flex>
      </AssetDetailsBaseCard>
    );
  }

  // Don't show this card if the MDM entitlement is missing
  if (!hasMdmEntitlement) {
    return null;
  }

  if (
    ithEnableNotApplicableMdmFlagForDevices &&
    deviceAsset.mdmStatusOverride === AssetsMdmStatus.NotApplicable
  ) {
    return (
      <AssetDetailsBaseCard
        title={t('assetDetailsContent.deviceManagement')}
        icon="laptop">
        <Flex p={space_m} minHeight="250px" alignItems="center">
          <DeviceNotApplicableBanner asset={deviceAsset} />
        </Flex>
      </AssetDetailsBaseCard>
    );
  }

  if (!systemWithPolicyStats) {
    return (
      <AssetDetailsBaseCard
        title={t('assetDetailsContent.deviceManagement')}
        icon="laptop">
        <Flex p={space_m} minHeight="250px" alignItems="center">
          <DeviceNotEnrolledBanner asset={deviceAsset} />
        </Flex>
      </AssetDetailsBaseCard>
    );
  }

  return (
    <AssetDetailsBaseCard
      title={t('assetDetailsContent.deviceManagement')}
      icon="laptop">
      <Flex
        width="100%"
        vertical={!(lg && fullWidth)}
        gap={space_m}
        mt={space_l}
        mb={space_s}>
        <DetailsContainer>
          <>
            <DetailSection
              icon="wrench"
              label={t('assetDetailsContent.managementTools')}>
              <AssetDetailItem
                label={t('assetDetailsContent.mdmActive')}
                value={
                  isMdmActive
                    ? t('assetDetailsContent.yes')
                    : t('assetDetailsContent.no')
                }
                labelTooltip={t('assetDetailsContent.mdmActiveTooltip')}
              />
              <AssetDetailItem
                label={t('assetDetailsContent.mdmCreatedAt')}
                value={formatDate(
                  systemWithPolicyStats?.system?.created,
                  'MM/dd/yyyy',
                  '-'
                )}
              />
              {isDesktopOrLaptopAsset(deviceAsset) &&
                !hideElectricAppInstalledIndicator && (
                  <AssetDetailItem
                    label={t('assetDetailsContent.electricForDesktopInstalled')}
                    value={
                      deviceAsset
                        ? getWasElectricAppInstalledOnDevice(deviceAsset)
                          ? t('assetDetailsContent.yes')
                          : t('assetDetailsContent.no')
                        : '-'
                    }
                  />
                )}
            </DetailSection>
            <Divider />

            <DetailSection
              icon="calendar-check"
              label={t('assetDetailsContent.lastCheckIn')}>
              <AssetDetailItem
                label={t('assetDetailsContent.fromMdm')}
                value={formatDate(
                  systemWithPolicyStats?.system?.lastContact,
                  'MM/dd/yyyy',
                  '-'
                )}
              />
              {isDesktopOrLaptopAsset(deviceAsset) && (
                <AssetDetailItem
                  label={t('assetDetailsContent.fromElectricForDesktop')}
                  value={formatDate(
                    deviceAsset.device?.reportingData?.lastOsqueryReport,
                    'MM/dd/yyyy',
                    '-'
                  )}
                  labelTooltip={t('assetDetailsContent.lastCheckInTooltip')}
                />
              )}
            </DetailSection>
            <Divider />
            <DetailSection
              icon="shield-check"
              label={t('assetDetailsContent.policies')}>
              <AssetDetailItem
                label={
                  <LinkNoUnderline
                    color="red"
                    variant="label-small"
                    href={getLocalizedSupportUrl(
                      'https://support.electric.ai/electricithub/electric-it-hub-jumpcloud-mdm-default-policies'
                    )}
                    target="_blank">
                    {t('assetDetailsContent.securityPolicies')}
                  </LinkNoUnderline>
                }
                value={
                  systemWithPolicyStats?.policyStats?.policyCountData?.total
                    ? `${systemWithPolicyStats?.policyStats?.policyCountData?.total} ${t('assetDetailsContent.total')}`
                    : '-'
                }
              />
              <AssetDetailItem
                label={t('assetDetailsContent.applied')}
                value={
                  systemWithPolicyStats?.policyStats?.policyCountData?.success?.toString() ??
                  '-'
                }
              />
              <AssetDetailItem
                label={t('assetDetailsContent.failed')}
                value={
                  systemWithPolicyStats?.policyStats?.policyCountData?.failed?.toString() ??
                  '-'
                }
              />
              <AssetDetailItem
                label={t('assetDetailsContent.pending')}
                value={
                  systemWithPolicyStats?.policyStats?.policyCountData?.pending?.toString() ??
                  '-'
                }
              />
              <AssetDetailCollapsible
                label={t('assetDetailsContent.detailedOsData')}
                collapsedText={osName}
                expandedText={getOsDetails(
                  systemWithPolicyStats?.system?.osVersionDetail,
                  t,
                  osDisplayNameFromReporting || undefined
                )}
              />
            </DetailSection>
          </>
        </DetailsContainer>
      </Flex>
    </AssetDetailsBaseCard>
  );
};

export default DeviceManagementDetailsCard;
